import { useMemo, useState } from 'react';
import { generateQRMatrix, qrToSVG } from '../lib/qr';
import './PairQR.css';

interface PairQRProps {
  /** The pair code the QR wraps into an invite link. */
  code: string;
  /** Prefix for test ids / aria ids. */
  idPrefix?: string;
}

/**
 * Scan-to-join QR (Jonathan email 2026-10-12: "QR code generation from
 * jitslab") — JitsLab's dependency-free generator ported to web/src/lib/qr.ts.
 *
 * The code encodes the same link the emailed invite carries
 * (/?pair_code=XXXXXX), so a partner pointing a camera at it lands on the
 * join form with the code already filled. Dark-on-light regardless of theme:
 * scannability beats palette — a QR that won't scan is decoration.
 *
 * Collapsed behind "Show QR" so the invite block stays copy-first; the QR is
 * the in-person path (same room, two phones), not the default one.
 */
export function PairQR({ code, idPrefix = 'pair-qr' }: PairQRProps) {
  const [open, setOpen] = useState(false);

  const svg = useMemo(() => {
    if (!open || !code) return null;
    const link = `${window.location.origin}/?pair_code=${encodeURIComponent(code)}`;
    try {
      return qrToSVG(generateQRMatrix(link), { size: 5, margin: 3 });
    } catch {
      return null; // past encoder capacity — hide rather than crash the screen
    }
  }, [open, code]);

  if (!code) return null;

  return (
    <div className="pear-qr" id={idPrefix}>
      <button
        type="button"
        className="pear-invite-btn pear-qr-toggle"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls={`${idPrefix}-svg`}
        id={`${idPrefix}-toggle`}
      >
        {open ? 'Hide QR' : 'Show QR'}
      </button>
      {svg && (
        <div
          className="pear-qr-frame"
          id={`${idPrefix}-svg`}
          role="img"
          aria-label="QR code — point your partner's camera here to open the invite"
          /* eslint-disable-next-line react/no-danger -- self-generated SVG */
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      )}
    </div>
  );
}
